import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { Panel } from 'primereact/panel';
import { Message } from 'primereact/message';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import api from '../../services/api';
import { revalidatePublicPath } from '../../utils/revalidatePublic';
import { tagOptions } from '../../constants/constants';
import MetaTagsLiveCodePreview from '../EntryPages/EntryPage/MetaTagsLiveCodePreview';
import ProseMirrorComponent from '../../components/AdvancedProseMirrorEditor';
import '../../components/styles.css';
import 'prosemirror-example-setup/style/style.css';
import 'prosemirror-menu/style/menu.css';
import {
  HOME_SEO_PACK_OPTIONS,
  buildHomeSeoPackTags,
  mergeHomeSeoPackTags,
} from './homeSeoPack';

const EMPTY_LANG = { eyebrow: '', title: '', description: '' };
const EMPTY_COPY = { es: { ...EMPTY_LANG }, en: { ...EMPTY_LANG } };
const EMPTY_STATES_SEE_MORE = {
  es: { country_page_id: '', url: '', title: '' },
  en: { country_page_id: '', url: '', title: '' },
};
const EMPTY_FAQ_SECTION = { h3: '', p: '', items: [] };
const EMPTY_FAQS = {
  es: { ...EMPTY_FAQ_SECTION, items: [] },
  en: { ...EMPTY_FAQ_SECTION, items: [] },
};

function normalizeLang(raw) {
  return { ...EMPTY_LANG, ...(raw || {}) };
}

function normalizeStatesSeeMoreLang(raw) {
  return {
    country_page_id: raw?.country_page_id ? String(raw.country_page_id) : '',
    url: raw?.url || '',
    title: raw?.title || '',
  };
}

function normalizeFaqsSection(raw) {
  return {
    h3: raw?.h3 || '',
    p: raw?.p || '',
    items: (Array.isArray(raw?.items) ? raw.items : []).map((faq) => ({
      question: faq.question || '',
      answer: faq.answer || '',
      answer_html: faq.answer_html || '',
      answer_html_updated: faq.answer_html || '',
    })),
  };
}

function convertToMetaTagsFormat(tags) {
  if (!Array.isArray(tags) || !tags.length) {
    return [{ attributes: [{ key: '', value: '' }] }];
  }
  return tags.map((tag) => {
    const attributesArray = Object.entries(tag.attributes || {}).map(([key, value]) => ({
      key: key.trim(),
      value: value == null ? '' : value.toString().trim(),
    }));
    const typeTag = tagOptions.find((option) => option.code === tag.type) || {};
    return {
      type: typeTag || {},
      content: tag.content || '',
      name: tag.name || '',
      attributes: attributesArray.length ? attributesArray : [{ key: '', value: '' }],
    };
  });
}

function toFinalMetaTags(metaTags) {
  return (metaTags || []).map((tag) => {
    const finalTag = {
      type: tag?.type?.code,
      name: tag.name || '',
      self_closing: tag?.type?.self_closing || false,
      content: tag?.content || '',
      attributes: {},
    };
    if (tag.attributes && Array.isArray(tag.attributes)) {
      tag.attributes.forEach((attr) => {
        if (attr.key && attr.key.trim() && attr.value !== undefined) {
          finalTag.attributes[attr.key.trim()] = attr.value.toString().trim();
        }
      });
    }
    return finalTag;
  });
}

function ActionBar({ onSave, onPublish, saving, publishing, disabled }) {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '720px',
        maxWidth: '95vw',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '1rem',
        backgroundColor: 'white',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
      }}
    >
      <Button
        type="button"
        label="Publish"
        icon="pi pi-cloud-upload"
        className="p-button-warning"
        loading={publishing}
        disabled={disabled || saving}
        onClick={onPublish}
        style={{ width: '200px' }}
      />
      <Button
        type="button"
        label="Save"
        icon="pi pi-save"
        className="main-button"
        loading={saving}
        disabled={disabled || publishing}
        onClick={onSave}
        style={{ width: '200px' }}
      />
    </div>
  );
}

function publicHomeUrl(base, path) {
  const origin = (base || 'https://bordergarita.com').replace(/\/$/, '');
  return `${origin}${path}`;
}

function PublicHomeLink({ path, publicUrl }) {
  const href = publicHomeUrl(publicUrl, path);
  return (
    <div className="mb-3">
      <Message
        severity="info"
        text="This link is available for public access."
        className="border-primary w-full justify-content-start mb-2"
      />
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="no-underline hover:opacity-80 transition-opacity"
      >
        <div className="flex align-items-center">
          <label className="text-md font-semibold mb-0 mr-2 cursor-pointer">
            <strong>{href}</strong>
          </label>
          <i className="pi pi-external-link" style={{ fontSize: '0.9rem' }} />
        </div>
      </a>
    </div>
  );
}

function LangEditor({ path, values, onChange }) {
  return (
    <Panel header={`Hero copy (${path})`} className="mb-3">
      <div className="mb-3">
        <label>
          <strong>Eyebrow</strong>
        </label>
        <InputText
          value={values.eyebrow || ''}
          onChange={(e) => onChange('eyebrow', e.target.value)}
          className="w-full"
          placeholder="Eyebrow"
        />
      </div>
      <div className="mb-3">
        <label>
          <strong>Title</strong>
        </label>
        <InputText
          value={values.title || ''}
          onChange={(e) => onChange('title', e.target.value)}
          className="w-full"
          placeholder="Home title"
        />
      </div>
      <div className="mb-3">
        <label>
          <strong>Description</strong>
        </label>
        <InputTextarea
          value={values.description || ''}
          onChange={(e) => onChange('description', e.target.value)}
          className="w-full"
          placeholder="Home description"
          rows={4}
          autoResize
        />
      </div>
    </Panel>
  );
}

function pageMatchesLang(page, langKey) {
  const langUpper = String(langKey || '').toUpperCase();
  if (!langUpper) return true;
  const pageLang = String(page?.lang || '').toUpperCase();
  if (pageLang && pageLang === langUpper) return true;

  const slug = String(page?.full_slug || page?.slug || '').toLowerCase();
  const prefix = `/${String(langKey || '').toLowerCase()}/`;
  if (slug.startsWith(prefix) || slug === `/${String(langKey || '').toLowerCase()}`) {
    return true;
  }
  return false;
}

function countryPageSlug(page) {
  const slug = page?.full_slug || page?.slug || '';
  if (!slug) return '';
  return slug.startsWith('/') ? slug : `/${slug}`;
}

function countryPageTitle(page) {
  return page?.title || page?.hero?.title || countryPageSlug(page) || '';
}

function toCountryPageOption(page) {
  const id = page?._id != null ? String(page._id) : '';
  const slug = countryPageSlug(page);
  const name = countryPageTitle(page);
  const label = name && slug && name !== slug ? `${slug} · ${name}` : slug || name || id;
  return { label, value: id, page };
}

function StatesSeeMoreEditor({
  path,
  langKey,
  values,
  countryPages,
  loadingPages,
  onReload,
  onChange,
}) {
  const options = useMemo(
    () =>
      (countryPages || [])
        .filter((p) => pageMatchesLang(p, langKey))
        .map(toCountryPageOption)
        .filter((opt) => opt.value),
    [countryPages, langKey]
  );

  const applyCountryPage = (pageId) => {
    const page = (countryPages || []).find((p) => String(p._id) === String(pageId));
    if (!pageId || !page) {
      onChange({ country_page_id: '', url: '', title: '' });
      return;
    }
    onChange({
      country_page_id: String(pageId),
      url: countryPageSlug(page),
      title: countryPageTitle(page),
    });
  };

  return (
    <Panel header={`States see more (${path})`} className="mb-3">
      <div className="mb-0">
        <label>
          <strong>Country page</strong>
        </label>
        <div className="flex gap-2">
          <Dropdown
            value={values?.country_page_id ? String(values.country_page_id) : null}
            options={options}
            optionLabel="label"
            optionValue="value"
            placeholder={
              options.length
                ? 'Select country page'
                : `No ${String(langKey || '').toUpperCase()} country pages`
            }
            className="w-full"
            filter
            filterBy="label"
            showClear
            disabled={loadingPages}
            onChange={(e) => applyCountryPage(e.value)}
          />
          <Button
            type="button"
            icon="pi pi-refresh"
            className="p-button-text"
            onClick={onReload}
            loading={loadingPages}
            aria-label="Reload country pages"
          />
        </div>
        {values?.url ? (
          <small className="text-600 block mt-1">
            VER MÁS → {values.url}
            {values.title ? ` · title “${values.title}”` : ''}
          </small>
        ) : (
          <small className="text-600 block mt-1">
            Link and title come from the selected country page. Same for every state and city.
          </small>
        )}
      </div>
    </Panel>
  );
}

function SeoPanel({
  langKey,
  metaTags,
  setMetaTags,
  collapsed,
  onToggleCollapsed,
  onOpenSeoPack,
  finalMetaTags,
}) {
  const addMetaTag = () => {
    const nextIndex = metaTags?.length || 0;
    const wasCollapsed = collapsed;
    setMetaTags([...(metaTags || []), { attributes: [{ key: '', value: '' }] }]);
    if (wasCollapsed) onToggleCollapsed(false);
    setTimeout(() => {
      document
        .getElementById(`seo-element-${langKey}-${nextIndex}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, wasCollapsed ? 180 : 80);
  };
  const removeMetaTag = (tagIndex) => setMetaTags(metaTags.filter((_, i) => i !== tagIndex));
  const addAttribute = (tagIndex) => {
    const updated = [...metaTags];
    updated[tagIndex] = {
      ...updated[tagIndex],
      attributes: [...(updated[tagIndex].attributes || []), { key: '', value: '' }],
    };
    setMetaTags(updated);
  };
  const removeAttribute = (tagIndex, attrIndex) => {
    const updated = [...metaTags];
    updated[tagIndex] = {
      ...updated[tagIndex],
      attributes: updated[tagIndex].attributes.filter((_, i) => i !== attrIndex),
    };
    setMetaTags(updated);
  };
  const updateAttribute = (tagIndex, attrIndex, field, newValue) => {
    const updated = [...metaTags];
    const attrs = [...(updated[tagIndex].attributes || [])];
    attrs[attrIndex] = { ...attrs[attrIndex], [field]: newValue };
    updated[tagIndex] = { ...updated[tagIndex], attributes: attrs };
    setMetaTags(updated);
  };
  const updateMainAttribute = (tagIndex, field, newValue) => {
    const updated = [...metaTags];
    updated[tagIndex] = { ...updated[tagIndex], [field]: newValue };
    setMetaTags(updated);
  };
  const updateTagType = (tagIndex, type) => {
    const updated = [...metaTags];
    updated[tagIndex] = { ...updated[tagIndex], type };
    setMetaTags(updated);
  };

  return (
    <Panel
      header="SEO & structured data"
      className="mb-3"
      toggleable
      collapsed={collapsed}
      onToggle={(e) => onToggleCollapsed(e.value)}
      expandIcon="pi pi-chevron-down"
      collapseIcon="pi pi-chevron-up"
    >
      <div className="flex flex-wrap justify-content-end align-items-center gap-2 mb-3">
        <Button
          type="button"
          label="Add SEO element"
          icon="pi pi-plus"
          className="w-auto"
          onClick={addMetaTag}
        />
        <Button
          type="button"
          label="Generate SEO batch"
          className="p-button-outlined w-auto"
          onClick={onOpenSeoPack}
        />
      </div>
      {metaTags?.map((tag, tagIndex) => (
        <div
          key={tagIndex}
          id={`seo-element-${langKey}-${tagIndex}`}
          className="p-3 mb-3 border-round border-1 surface-border"
        >
          <div className="flex justify-content-between align-items-center mb-3">
            <h4 className="m-0">
              SEO element {tagIndex + 1}
              {tag.name ? (
                <span className="ml-2 text-sm font-normal text-color-secondary">
                  ({tag.name})
                </span>
              ) : null}
            </h4>
            <Button
              icon="pi pi-trash"
              className="p-button-danger p-button-sm"
              type="button"
              onClick={() => removeMetaTag(tagIndex)}
            />
          </div>
          <label>
            <strong>Type</strong>
          </label>
          <Dropdown
            value={tag.type}
            options={tagOptions}
            filter
            filterBy="label"
            filterInputAutoFocus
            optionLabel="label"
            onChange={(e) => updateTagType(tagIndex, e.value)}
            placeholder="Select a type"
            className="w-full mb-2"
          />
          <label>
            <strong>Content</strong>
          </label>
          <InputTextarea
            value={tag.content || ''}
            onChange={(e) => updateMainAttribute(tagIndex, 'content', e.target.value)}
            rows={1}
            autoResize
            className="w-full mb-2"
          />
          {tag.attributes?.map((attr, attrIndex) => (
            <div key={attrIndex} className="flex gap-2 mb-2">
              <InputText
                value={attr.key}
                placeholder="key"
                onChange={(e) => updateAttribute(tagIndex, attrIndex, 'key', e.target.value)}
                className="w-full"
              />
              <InputText
                value={attr.value}
                placeholder="value"
                onChange={(e) => updateAttribute(tagIndex, attrIndex, 'value', e.target.value)}
                className="w-full"
              />
              <Button
                icon="pi pi-times"
                type="button"
                className="p-button-text p-button-danger"
                onClick={() => removeAttribute(tagIndex, attrIndex)}
              />
            </div>
          ))}
          <Button
            type="button"
            label="Add Attribute"
            icon="pi pi-plus"
            className="p-button-text p-button-sm"
            onClick={() => addAttribute(tagIndex)}
          />
        </div>
      ))}
      <div className="mt-3">
        <MetaTagsLiveCodePreview metaTags={finalMetaTags} />
      </div>
    </Panel>
  );
}

function FaqRichTextEditor({ content, onChange, active, editorKey }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!active) {
      setReady(false);
      return undefined;
    }
    const t = window.setTimeout(() => setReady(true), 50);
    return () => {
      window.clearTimeout(t);
      setReady(false);
    };
  }, [active]);

  if (!active || !ready) {
    return <div className="prosemirror-wrapper" style={{ minHeight: 220 }} />;
  }

  return (
    <ProseMirrorComponent key={editorKey} content={content} onChange={onChange} />
  );
}

function FaqsPanel({ langKey, faqs, setFaqs, collapsed, onToggleCollapsed }) {
  const items = faqs.items || [];
  const editorsActive = !collapsed;

  const handleAddFaq = () => {
    const nextIndex = items.length;
    setFaqs((prev) => ({
      ...prev,
      items: [
        ...(prev.items || []),
        { question: '', answer: '', answer_html: '', answer_html_updated: '' },
      ],
    }));
    if (collapsed) onToggleCollapsed(false);
    setTimeout(() => {
      document.getElementById(`faq-question-${langKey}-${nextIndex}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, collapsed ? 180 : 50);
  };

  const handleRemoveFaq = (idx) => {
    setFaqs((prev) => ({
      ...prev,
      items: (prev.items || []).filter((_, i) => i !== idx),
    }));
  };

  const handleFaqChange = (idx, field, value) => {
    setFaqs((prev) => {
      const list = [...(prev.items || [])];
      list[idx] = { ...list[idx], [field]: value };
      return { ...prev, items: list };
    });
  };

  return (
    <Panel
      header="Manage FAQs"
      toggleable
      collapsed={collapsed}
      onToggle={(e) => onToggleCollapsed(e.value)}
      expandIcon="pi pi-chevron-down"
      collapseIcon="pi pi-chevron-up"
      transitionOptions={{ timeout: 0 }}
    >
      <div className="grid">
        <div className="col-12">
          <label>
            <strong>FAQ section title (h3)</strong>
          </label>
          <InputText
            value={faqs.h3 || ''}
            onChange={(e) => setFaqs((prev) => ({ ...prev, h3: e.target.value }))}
            className="w-full"
            placeholder="Heading above the FAQ list"
          />
        </div>
        <div className="col-12">
          <label>
            <strong>FAQ section description</strong>
          </label>
          <InputTextarea
            value={faqs.p || ''}
            onChange={(e) => setFaqs((prev) => ({ ...prev, p: e.target.value }))}
            rows={2}
            className="w-full"
            placeholder="Short intro under the heading"
          />
        </div>
        {items.length === 0 && (
          <div className="col-12">
            <Message
              severity="info"
              className="w-full"
              text="Title and description are the section header. Click Add FAQ and fill Question + Answer — those items feed Schema · FAQPage."
            />
          </div>
        )}
        <div className="flex justify-content-end mb-3 col-12">
          <Button
            type="button"
            label="Add FAQ"
            icon="pi pi-plus"
            className="w-auto"
            onClick={handleAddFaq}
          />
        </div>
        {items.map((faq, idx) => (
          <div key={idx} className="p-3 mb-2 border-1 border-round surface-border col-12">
            <div className="flex justify-content-between align-items-center mb-2">
              <strong>Question {idx + 1}</strong>
              <Button
                icon="pi pi-trash"
                type="button"
                className="p-button-danger p-button-text"
                onClick={() => handleRemoveFaq(idx)}
              />
            </div>
            <InputText
              id={`faq-question-${langKey}-${idx}`}
              value={faq.question || ''}
              placeholder="Question"
              className="w-full mb-2"
              onChange={(e) => handleFaqChange(idx, 'question', e.target.value)}
            />
            <InputTextarea
              value={faq.answer || ''}
              placeholder="Answer"
              rows={3}
              className="w-full mb-2"
              autoResize
              onChange={(e) => handleFaqChange(idx, 'answer', e.target.value)}
            />
            <label>
              <strong>Rich text</strong>
            </label>
            <FaqRichTextEditor
              editorKey={`home-faq-pm-${langKey}-${idx}`}
              active={editorsActive}
              content={faq.answer_html || ''}
              onChange={(htmlContent) => handleFaqChange(idx, 'answer_html_updated', htmlContent)}
            />
          </div>
        ))}
      </div>
    </Panel>
  );
}

export default function HomePagesManager() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copy, setCopy] = useState(EMPTY_COPY);
  const [statesSeeMore, setStatesSeeMore] = useState(EMPTY_STATES_SEE_MORE);
  const [tagsByLang, setTagsByLang] = useState({
    es: [{ attributes: [{ key: '', value: '' }] }],
    en: [{ attributes: [{ key: '', value: '' }] }],
  });
  const [faqsByLang, setFaqsByLang] = useState(EMPTY_FAQS);
  const [seoPanelCollapsed, setSeoPanelCollapsed] = useState({ es: true, en: true });
  const [faqsPanelCollapsed, setFaqsPanelCollapsed] = useState({ es: true, en: true });
  const [seoPackDialogVisible, setSeoPackDialogVisible] = useState(false);
  const [seoPackLang, setSeoPackLang] = useState('es');
  const [seoPackSelected, setSeoPackSelected] = useState([]);
  const [seoPackGenerating, setSeoPackGenerating] = useState(false);
  const [countryPages, setCountryPages] = useState([]);
  const [loadingCountryPages, setLoadingCountryPages] = useState(false);

  const publicUrl = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL || '';

  const finalTagsByLang = useMemo(
    () => ({
      es: toFinalMetaTags(tagsByLang.es),
      en: toFinalMetaTags(tagsByLang.en),
    }),
    [tagsByLang]
  );

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setHydrated(false);
    setLoadError('');
    try {
      const res = await api.get('/home_pages');
      setCopy({
        es: normalizeLang(res?.data?.copy?.es),
        en: normalizeLang(res?.data?.copy?.en),
      });
      setStatesSeeMore({
        es: normalizeStatesSeeMoreLang(res?.data?.states_see_more?.es),
        en: normalizeStatesSeeMoreLang(res?.data?.states_see_more?.en),
      });
      setTagsByLang({
        es: convertToMetaTagsFormat(res?.data?.tags?.es),
        en: convertToMetaTagsFormat(res?.data?.tags?.en),
      });
      setFaqsByLang({
        es: normalizeFaqsSection(res?.data?.faqs?.es),
        en: normalizeFaqsSection(res?.data?.faqs?.en),
      });
      setHydrated(true);
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.error || err?.message || 'Could not load home pages';
      setLoadError(detail);
      toast.current?.show({
        severity: 'error',
        summary: 'Load error',
        detail: `${detail}. Save/Publish disabled until reload succeeds.`,
        life: 6000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const loadCountryPages = useCallback(async () => {
    setLoadingCountryPages(true);
    try {
      const res = await api.get('/country_pages/summary');
      setCountryPages(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('HomePagesManager: failed to load country pages', err);
      setCountryPages([]);
    } finally {
      setLoadingCountryPages(false);
    }
  }, []);

  useEffect(() => {
    loadCountryPages();
  }, [loadCountryPages]);

  const updateLang = (langKey, field, value) => {
    setCopy((prev) => ({
      ...prev,
      [langKey]: {
        ...prev[langKey],
        [field]: value,
      },
    }));
  };

  const serializeFaqs = (section) => ({
    h3: section?.h3 || '',
    p: section?.p || '',
    items: (section?.items || []).map((faq) => ({
      question: faq.question || '',
      answer: faq.answer || '',
      answer_html: faq.answer_html_updated || faq.answer_html || '',
    })),
  });

  const onSave = async ({ silent } = {}) => {
    if (!hydrated) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Not loaded',
        detail: 'Home pages did not load. Retry before saving — an empty save would wipe /es and /en.',
        life: 5000,
      });
      return false;
    }

    const tagsPayload = {
      es: (finalTagsByLang.es || []).filter((tag) => tag?.type),
      en: (finalTagsByLang.en || []).filter((tag) => tag?.type),
    };
    const copyHasText = ['es', 'en'].some((lang) =>
      ['eyebrow', 'title', 'description'].some((field) => (copy?.[lang]?.[field] || '').trim())
    );
    const faqsHaveItems = ['es', 'en'].some((lang) => (faqsByLang[lang]?.items || []).length > 0);
    if (!copyHasText && !tagsPayload.es.length && !tagsPayload.en.length && !faqsHaveItems) {
      toast.current?.show({
        severity: 'error',
        summary: 'Blocked empty save',
        detail: 'Title, SEO tags and FAQs are empty. Refusing to overwrite production homes.',
        life: 6000,
      });
      return false;
    }

    setSaving(true);
    try {
      const payload = {
        copy,
        states_see_more: {
          es: normalizeStatesSeeMoreLang(statesSeeMore.es),
          en: normalizeStatesSeeMoreLang(statesSeeMore.en),
        },
        tags: tagsPayload,
        faqs: {
          es: serializeFaqs(faqsByLang.es),
          en: serializeFaqs(faqsByLang.en),
        },
      };
      const res = await api.put('/home_pages', payload);
      setCopy({
        es: normalizeLang(res?.data?.copy?.es),
        en: normalizeLang(res?.data?.copy?.en),
      });
      setStatesSeeMore({
        es: normalizeStatesSeeMoreLang(res?.data?.states_see_more?.es),
        en: normalizeStatesSeeMoreLang(res?.data?.states_see_more?.en),
      });
      setTagsByLang({
        es: convertToMetaTagsFormat(res?.data?.tags?.es),
        en: convertToMetaTagsFormat(res?.data?.tags?.en),
      });
      setFaqsByLang({
        es: normalizeFaqsSection(res?.data?.faqs?.es),
        en: normalizeFaqsSection(res?.data?.faqs?.en),
      });
      if (res?.data?.preserved?.length) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Kept existing content',
          detail: `Empty overwrite blocked for: ${res.data.preserved.join(', ')}`,
          life: 5000,
        });
      } else if (!silent) {
        toast.current?.show({
          severity: 'success',
          summary: 'Saved',
          detail: 'Home pages saved',
          life: 3000,
        });
      }
      return true;
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: 'error',
        summary: 'Save error',
        detail: err?.response?.data?.error || err?.message || 'Could not save',
        life: 4000,
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onPublish = async () => {
    const saved = await onSave({ silent: true });
    if (!saved) return;

    const secret = process.env.REACT_APP_BORDERGARITA_REVALIDATE_SECRET || '';
    if (!publicUrl || !secret) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Saved, not published',
        detail: 'Missing REACT_APP_BORDERGARITA_PUBLIC_APP_URL or REVALIDATE_SECRET',
        life: 5000,
      });
      return;
    }

    setPublishing(true);
    try {
      const results = await Promise.allSettled(
        ['/es', '/en'].map((path) => revalidatePublicPath(publicUrl, secret, path))
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;

      toast.current?.show({
        severity: failed ? 'warn' : 'success',
        summary: 'Published',
        detail: failed
          ? `Saved + revalidated ${ok}/2 paths (${failed} failed)`
          : 'Saved + revalidated /es and /en',
        life: 4500,
      });
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: 'error',
        summary: 'Publish error',
        detail: err?.message || 'Revalidation failed',
        life: 4000,
      });
    } finally {
      setPublishing(false);
    }
  };

  const handleGenerateSeoPack = () => {
    if (!seoPackSelected.length) {
      toast.current?.show({
        severity: 'warn',
        summary: 'SEO pack',
        detail: 'Select at least one pack',
        life: 3000,
      });
      return;
    }
    setSeoPackGenerating(true);
    try {
      const langKey = seoPackLang;
      const hero = copy[langKey] || {};
      const altLang = langKey === 'es' ? 'en' : 'es';
      const siteUrl = (publicUrl || 'https://bordergarita.com').replace(/\/$/, '');
      const faqItems = faqsByLang[langKey]?.items || [];
      const faqQuestions = faqItems.filter((faq) => (faq?.question || '').trim());

      if (seoPackSelected.includes('faq') && !faqQuestions.length) {
        toast.current?.show({
          severity: 'warn',
          summary: 'SEO pack',
          detail: `FAQ title/description is the section header. Click Add FAQ and fill a Question on ${langKey === 'es' ? '/es' : '/en'} first.`,
          life: 4500,
        });
        if (seoPackSelected.length === 1) {
          return;
        }
      }

      const incoming = buildHomeSeoPackTags(
        {
          title: hero.title || '',
          description: hero.description || '',
          image: '',
          full_slug: `/${langKey}`,
          lang: langKey,
          translations: [{ lang: altLang, full_slug: `/${altLang}` }],
          faqs: faqItems,
          siteUrl,
        },
        seoPackSelected
      ).map((tag) => {
        const matchedType = tagOptions.find((o) => o.code === tag.type?.code);
        return matchedType ? { ...tag, type: matchedType } : tag;
      });

      const replaceNames =
        seoPackSelected.includes('faq') && faqQuestions.length ? ['seo:schema:faq'] : [];
      const { tags, added, skipped } = mergeHomeSeoPackTags(
        tagsByLang[langKey],
        incoming,
        replaceNames
      );
      setTagsByLang((prev) => ({
        ...prev,
        [langKey]: tags.length ? tags : [{ attributes: [{ key: '', value: '' }] }],
      }));
      setSeoPanelCollapsed((prev) => ({ ...prev, [langKey]: false }));
      setSeoPackDialogVisible(false);
      toast.current?.show({
        severity: added ? 'success' : 'info',
        summary: 'SEO pack',
        detail:
          skipped > 0
            ? `Added ${added} tag(s). Skipped ${skipped} (already present).`
            : `Added ${added} tag(s).`,
        life: 4000,
      });
    } finally {
      setSeoPackGenerating(false);
    }
  };

  const renderLangTab = (langKey, path) => (
    <>
      <PublicHomeLink path={path} publicUrl={publicUrl} />
      <LangEditor
        path={path}
        values={copy[langKey]}
        onChange={(field, value) => updateLang(langKey, field, value)}
      />
      <StatesSeeMoreEditor
        path={path}
        langKey={langKey}
        values={statesSeeMore[langKey]}
        countryPages={countryPages}
        loadingPages={loadingCountryPages}
        onReload={loadCountryPages}
        onChange={(patch) =>
          setStatesSeeMore((prev) => ({
            ...prev,
            [langKey]: { ...prev[langKey], ...patch },
          }))
        }
      />
      <SeoPanel
        langKey={langKey}
        metaTags={tagsByLang[langKey]}
        setMetaTags={(next) =>
          setTagsByLang((prev) => ({
            ...prev,
            [langKey]: typeof next === 'function' ? next(prev[langKey]) : next,
          }))
        }
        collapsed={seoPanelCollapsed[langKey]}
        onToggleCollapsed={(value) =>
          setSeoPanelCollapsed((prev) => ({ ...prev, [langKey]: value }))
        }
        onOpenSeoPack={() => {
          setSeoPackLang(langKey);
          setSeoPackSelected([]);
          setSeoPackDialogVisible(true);
        }}
        finalMetaTags={finalTagsByLang[langKey]}
      />
      <FaqsPanel
        langKey={langKey}
        faqs={faqsByLang[langKey]}
        setFaqs={(next) =>
          setFaqsByLang((prev) => ({
            ...prev,
            [langKey]: typeof next === 'function' ? next(prev[langKey]) : next,
          }))
        }
        collapsed={faqsPanelCollapsed[langKey]}
        onToggleCollapsed={(value) =>
          setFaqsPanelCollapsed((prev) => ({ ...prev, [langKey]: value }))
        }
      />
    </>
  );

  return (
    <div className="p-3" style={{ paddingBottom: '6rem' }}>
      <Toast ref={toast} />
      <Card
        title="Home pages"
        subTitle="Hero copy, states see more, SEO and FAQs on /es and /en. Country pages have their own copy in Country pages."
      >
        <Message
          severity="info"
          className="w-full mb-3"
          text="Only /es and /en. Generate SEO batch uses this tab's Title and Description. For /{lang}/{country} edit the country page."
        />

        {loading ? (
          <p>Loading…</p>
        ) : loadError ? (
          <>
            <Message
              severity="error"
              className="w-full mb-3"
              text={`Could not load home pages (${loadError}). Save and Publish are disabled so an empty form cannot wipe /es and /en.`}
            />
            <Button
              type="button"
              label="Retry load"
              icon="pi pi-refresh"
              onClick={loadConfig}
            />
          </>
        ) : (
          <>
            <TabView>
              <TabPanel header="/es">{renderLangTab('es', '/es')}</TabPanel>
              <TabPanel header="/en">{renderLangTab('en', '/en')}</TabPanel>
            </TabView>
            <ActionBar
              onSave={() => onSave()}
              onPublish={onPublish}
              saving={saving}
              publishing={publishing}
              disabled={!hydrated}
            />
          </>
        )}
      </Card>

      <Dialog
        header="Generate SEO batch"
        visible={seoPackDialogVisible}
        onHide={() => setSeoPackDialogVisible(false)}
        style={{ width: '32rem', maxWidth: '95vw' }}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              className="p-button-text"
              onClick={() => setSeoPackDialogVisible(false)}
              disabled={seoPackGenerating}
            />
            <Button
              type="button"
              label="Generate"
              icon="pi pi-check"
              onClick={handleGenerateSeoPack}
              loading={seoPackGenerating}
            />
          </div>
        }
      >
        <p className="mt-0 mb-3 text-sm text-color-secondary">
          Creates meta/schema tags from this home&apos;s Title and Description
          ({seoPackLang === 'es' ? '/es' : '/en'}) and merges them (skips packs already present).
        </p>
        <div className="flex flex-column gap-3">
          {HOME_SEO_PACK_OPTIONS.map((pack) => (
            <div key={pack.id} className="flex align-items-start gap-2">
              <Checkbox
                inputId={`home-seo-pack-${pack.id}`}
                checked={seoPackSelected.includes(pack.id)}
                onChange={(e) => {
                  setSeoPackSelected((prev) => {
                    if (e.checked) {
                      return prev.includes(pack.id) ? prev : [...prev, pack.id];
                    }
                    return prev.filter((id) => id !== pack.id);
                  });
                }}
              />
              <label htmlFor={`home-seo-pack-${pack.id}`} className="cursor-pointer">
                <div className="font-medium">{pack.label}</div>
                <div className="text-sm text-color-secondary">{pack.description}</div>
              </label>
            </div>
          ))}
        </div>
      </Dialog>
    </div>
  );
}
